import type { BriefingStorySnapshot } from '../types';

type BriefingStoryCardProps = {
  story: BriefingStorySnapshot;
  position: number;
  variant?: 'hero' | 'standard';
};

function ProviderBadge({
  provider,
  position,
}: {
  provider: string;
  position: number;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <span
        aria-label={`Story ${position}`}
        className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-600 text-white text-xs font-bold flex items-center justify-center"
      >
        {position}
      </span>
      <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
        {provider}
      </span>
    </div>
  );
}

function HeroCard({ story, position }: BriefingStoryCardProps) {
  return (
    <a
      href={story.link}
      target="_blank"
      rel="noopener noreferrer"
      className="group block rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800/60 overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2">
        {story.imageUrl && (
          <div className="aspect-[4/3] lg:aspect-auto lg:min-h-[360px] overflow-hidden bg-neutral-100 dark:bg-neutral-900">
            <img
              src={story.imageUrl}
              alt=""
              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
              loading="eager"
            />
          </div>
        )}
        <div className="p-6 sm:p-8 lg:p-10 flex flex-col justify-center">
          <ProviderBadge provider={story.provider} position={position} />
          <h2 className="mt-4 text-2xl sm:text-3xl font-bold text-neutral-900 dark:text-white leading-tight group-hover:text-primary dark:group-hover:text-primary transition-colors">
            {story.title}
          </h2>
          <p className="mt-4 text-base text-neutral-600 dark:text-neutral-300 leading-relaxed">
            {story.summary}
          </p>
          <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 dark:text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            Read full story
            <span aria-hidden="true">→</span>
          </div>
        </div>
      </div>
    </a>
  );
}

function StandardCard({ story, position }: BriefingStoryCardProps) {
  return (
    <a
      href={story.link}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col sm:flex-row rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800/60 overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300"
    >
      {story.imageUrl && (
        <div className="sm:w-56 md:w-64 flex-shrink-0 aspect-video sm:aspect-auto overflow-hidden bg-neutral-100 dark:bg-neutral-900">
          <img
            src={story.imageUrl}
            alt=""
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            loading="lazy"
          />
        </div>
      )}
      <div className="flex-1 p-5 sm:p-6 flex flex-col justify-center min-w-0">
        <ProviderBadge provider={story.provider} position={position} />
        <h2 className="mt-3 text-lg font-bold text-neutral-900 dark:text-white leading-snug group-hover:text-primary dark:group-hover:text-primary transition-colors">
          {story.title}
        </h2>
        <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed">
          {story.summary}
        </p>
      </div>
    </a>
  );
}

export function BriefingStoryCard({
  story,
  position,
  variant = 'standard',
}: BriefingStoryCardProps) {
  if (variant === 'hero') {
    return <HeroCard story={story} position={position} />;
  }
  return <StandardCard story={story} position={position} />;
}
